import React, { useState, useEffect, useRef } from "react";
import { getSearchEquipment } from "../../../api/storeAPI";
import { getPrograms } from "../../../api/programsAPI";
import { getPricingTiers } from "../../../api/SettingsAPI";
import { useTheme } from "../../../context/ThemeContext";
import {
  getInputStyles,
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
} from "../../../utils/themeUtils";
import { captureApiError } from "../../../utils/sentry";
import { safeGetItem } from "@mediatechgroup/app-version";

interface Product {
  id: string;
  name: string;
  category?: string;
  price?: number;
}

interface ProductAutocompleteProps {
  value: string;
  onChange: (value: string, product?: Product | null) => void;
}

const ProductAutocomplete: React.FC<ProductAutocompleteProps> = ({
  value,
  onChange,
}) => {
  const { isDarkMode } = useTheme();
  const [query, setQuery] = useState(value);
  const [filtered, setFiltered] = useState<Product[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);
  const [pricingTiers, setPricingTiers] = useState<any[]>([]);
  const token = safeGetItem("authToken");
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch programs and pricing tiers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch programs
        const programsResponse = await getPrograms();
        const programsData = programsResponse?.data?.data || [];
        setPrograms(programsData);

        // Fetch pricing tiers
        const pricingResponse = await getPricingTiers();
        const pricingData = pricingResponse?.data || [];
        setPricingTiers(pricingData);
      } catch (err) {
        captureApiError(err, "fetchAutocompleteData", { component: "ProductAutocomplete" });
      }
    };
    fetchData();
  }, []);

  // Helper function to get price based on hours_per_week
  const getPriceByHours = (hours: number): number => {
    const tier = pricingTiers.find((t: any) => t.hours === hours);
    return tier ? parseFloat(tier.price) : 0;
  };

  const handelSearch = async () => {
    if (!query?.trim()) return setFiltered([]);

    try {
      setLoading(true);

      // Search products
      const productResults = await getSearchEquipment(token, query);
      const products = productResults?.data || [];

      // Filter programs by query
      const queryLower = query.toLowerCase();
      const matchingPrograms = programs
        .filter((program: any) =>
          program.label?.toLowerCase().includes(queryLower) ||
          program.name?.toLowerCase().includes(queryLower)
        )
        .map((program: any) => {
          const price = getPriceByHours(program.hours_per_week);
          const mappedProgram = {
            id: program.id,
            name: program.label || program.name,
            category: "Program",
            price: price,
          };
          return mappedProgram;
        });

      // Combine products and programs
      const combined = [...products, ...matchingPrograms];
      setFiltered(combined);
    } catch (err) {
      captureApiError(err, "fetchProducts", { component: "ProductAutocomplete" });
    } finally {
      setLoading(false);
    }
  };

  // debounce search
  useEffect(() => {
    if (!showDropdown) return; // only search when dropdown is open
    const delayDebounce = setTimeout(() => {
      handelSearch();
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [query, showDropdown]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item: Product | null) => {
    if (item) {
      setQuery(item.name);
      onChange(item.name, item);
    } else {
      const newProduct: Product = {
        id: "other",
        name: query,
        category: "Other",
      };
      setQuery(query);
      onChange(query, newProduct);
    }

    setShowDropdown(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input */}
      <input
        aria-label="Select or type product"
        type="text"
        value={query}
        onFocus={() => setShowDropdown(true)} // ✅ open dropdown when user focuses
        onChange={(e) => {
          setQuery(e.target.value);
          setShowDropdown(true); // ✅ open dropdown only when user types
          onChange(e.target.value, null);
        }}
        className={`w-full p-2 rounded focus:outline-none ${getInputStyles(
          isDarkMode
        )}`}
        placeholder="Select or type product"
      />

      {/* Dropdown */}
      {showDropdown && (
        <div
          className={`absolute z-10 ${getCardBackground(
            isDarkMode
          )} border ${
            isDarkMode ? "border-gray-600" : "border-gray-300"
          } rounded shadow-lg w-full max-h-48 overflow-y-auto`}
        >
          {loading ? (
            <div className="flex justify-center items-center py-3">
              <div
                className={`w-5 h-5 border-2 ${
                  isDarkMode ? "border-gray-500" : "border-gray-400"
                } border-t-transparent rounded-full animate-spin`}
              ></div>
              <span className={`ml-2 text-sm ${getSecondaryText(isDarkMode)}`}>
                Loading...
              </span>
            </div>
          ) : filtered?.length > 0 ? (
            filtered?.map((p) => (
              <div
                key={p.id}
                onClick={() => handleSelect(p)}
                className={`px-3 py-2 cursor-pointer ${
                  isDarkMode
                    ? "hover:bg-gray-700"
                    : "hover:bg-gray-100"
                }`}
              >
                <div className={`font-medium ${getPrimaryText(isDarkMode)}`}>
                  {p.name}
                </div>
                {p.category && (
                  <div className={`text-xs ${getSecondaryText(isDarkMode)}`}>
                    {p.category}
                  </div>
                )}
              </div>
            ))
          ) : query?.trim() !== "" ? (
            <div
              onClick={() => handleSelect(null)}
              className={`px-3 py-2 cursor-pointer text-sm ${
                isDarkMode
                  ? "hover:bg-gray-700 text-gray-400"
                  : "hover:bg-gray-100 text-gray-600"
              }`}
            >
              + Use "{query}" as Other
            </div>
          ) : (
            <div className={`px-3 py-2 text-sm ${getMutedText(isDarkMode)}`}>
              Start typing to search...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductAutocomplete;
